
import React, { useEffect, useMemo, useState } from 'react';
import { HashRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import CustomerPortal from './components/CustomerPortal';
import PublicLandingPage from './components/PublicLandingPage';
import TechDashboard from './components/TechDashboard';
import TSFADAnalysis from './components/TSFADAnalysis';
import TSFADRemoteFlow from './components/TSFADRemoteFlow';
import RepairTask from './components/RepairTask';
import InstallPrepTask from './components/InstallPrepTask';
import InstallExecutionTask from './components/InstallExecutionTask';
import InstallVerificationTask from './components/InstallVerificationTask';
import InstallDecommissionTask from './components/InstallDecommissionTask';
import CheckUpTask from './components/CheckUpTask';
import PreventativeMaintenanceTask from './components/PreventativeMaintenanceTask';
import SafetyUpgradeTask from './components/SafetyUpgradeTask';
import SafetyUpgradeImplementationTask from './components/SafetyUpgradeImplementationTask';
import InAppDialogHost from './components/InAppDialogHost';
import PlatformSupportConsole from './components/PlatformSupportConsole';
import UserActivityTracker from './components/UserActivityTracker';
import { useAuth } from './context/AuthContext';
import { consumeLoginNotice } from './services/auth';
import './App.css';

const LOGIN_ROUTE = '/login';
const PORTAL_ROUTE = '/portal';
const CUSTOMER_PORTAL_ROUTE = '/customer';
const PLATFORM_SUPPORT_ROUTE = '/platform-support';
const LOGIN_ORG_SLUG_STORAGE_KEY = 'fieldservice.login.organizationSlug';

function loadRememberedOrganizationSlug(): string {
  try {
    return (window.localStorage.getItem(LOGIN_ORG_SLUG_STORAGE_KEY) || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

function rememberOrganizationSlug(value: string): void {
  try {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) {
      window.localStorage.removeItem(LOGIN_ORG_SLUG_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(LOGIN_ORG_SLUG_STORAGE_KEY, normalized);
  } catch {
    // no-op: localStorage can be unavailable in private contexts
  }
}

function defaultRouteForRole(role: string | null | undefined): string {
  return role === 'CUSTOMER' ? CUSTOMER_PORTAL_ROUTE : '/tech';
}

const LoginPage: React.FC = () => {
  const { login, bootstrap, isAuthenticated, user } = useAuth();
  const parseInviteParams = () => {
    try {
      const hash = window.location.hash || '';
      const queryIndex = hash.indexOf('?');
      if (queryIndex === -1) return { organizationSlug: '', username: '' };
      const params = new URLSearchParams(hash.slice(queryIndex + 1));
      const org = (params.get('organizationSlug') || '').trim().toLowerCase();
      const username = (params.get('username') || '').trim().toLowerCase();
      return {
        organizationSlug: org,
        username
      };
    } catch {
      return { organizationSlug: '', username: '' };
    }
  };
  const inviteParams = useMemo(parseInviteParams, []);
  const [organizationSlug, setOrganizationSlug] = useState(
    inviteParams.organizationSlug || loadRememberedOrganizationSlug()
  );
  const [organizationName, setOrganizationName] = useState('');
  const [username, setUsername] = useState(inviteParams.username);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [licenseActivationKey, setLicenseActivationKey] = useState('');
  const [mode, setMode] = useState<'signin' | 'bootstrap'>('signin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    setNotice(consumeLoginNotice());
  }, []);

  const redirectPath = defaultRouteForRole(user?.role);
  if (isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const formData = new FormData(event.currentTarget);
      const orgSlug = String(formData.get('organizationSlug') ?? organizationSlug).trim().toLowerCase();
      const submittedUsername = String(formData.get('username') ?? username).trim().toLowerCase();
      const submittedPassword = String(formData.get('password') ?? password).trim();
      if (!orgSlug) {
        setError('Unit login slug is required.');
        return;
      }
      setOrganizationSlug(orgSlug);
      setUsername(submittedUsername);
      if (mode === 'bootstrap') {
        await bootstrap({
          organizationName: organizationName.trim(),
          organizationSlug: orgSlug,
          username: submittedUsername,
          password: submittedPassword,
          fullName: fullName.trim() || undefined,
          email: email.trim().toLowerCase() || undefined,
          licenseActivationKey: licenseActivationKey.trim()
        });
      } else {
        await login({
          organizationSlug: orgSlug,
          username: submittedUsername,
          password: submittedPassword
        });
      }
      rememberOrganizationSlug(orgSlug);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.loginWrap}>
      {error && (
        <div style={styles.loginErrorOverlay}>
          <section
            style={styles.loginErrorCard}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="login-error-dialog-title"
            aria-describedby="login-error-dialog-message"
          >
            <h2 id="login-error-dialog-title" style={styles.loginErrorTitle}>Sign in could not be completed</h2>
            <div id="login-error-dialog-message" style={styles.error}>{error}</div>
            <button style={styles.button} type="button" onClick={() => setError(null)}>
              Close
            </button>
          </section>
        </div>
      )}
      <form style={styles.loginCard} onSubmit={submit}>
        <div style={styles.loginEyebrow}>{mode === 'bootstrap' ? 'NEW STANDALONE UNIT' : 'BONZAIQ FIELD SERVICE'}</div>
        <h1 style={styles.loginTitle}>{mode === 'bootstrap' ? 'Create your unit' : 'Welcome back'}</h1>
        <p style={styles.loginSubtitle}>
          {mode === 'bootstrap'
            ? 'Start a clean, independent unit with its first admin account.'
            : 'Sign in with your unit, username, and password.'}
        </p>
        <div style={styles.fieldRow}>
          <button
            type="button"
            style={{ ...styles.toggleButton, ...(mode === 'signin' ? styles.toggleButtonActive : {}) }}
            onClick={() => {
              setMode('signin');
              setOrganizationSlug(loadRememberedOrganizationSlug());
              setError(null);
            }}
          >
            Sign in
          </button>
          <button
            type="button"
            style={{ ...styles.toggleButton, ...(mode === 'bootstrap' ? styles.toggleButtonActive : {}) }}
            onClick={() => {
              setMode('bootstrap');
              setOrganizationSlug('');
              setError(null);
            }}
          >
            Create unit
          </button>
        </div>
        {mode === 'bootstrap' && (
          <>
            <label style={styles.label}>Unit name</label>
            <input
              style={styles.input}
              value={organizationName}
              onChange={event => setOrganizationName(event.target.value)}
              autoComplete="organization-title"
            />
          </>
        )}
        <label style={styles.label}>Unit login slug</label>
        <input
          style={styles.input}
          name="organizationSlug"
          value={organizationSlug}
          onChange={event => setOrganizationSlug(event.target.value)}
          autoComplete={mode === 'bootstrap' ? 'off' : 'organization'}
          placeholder="for example: bangkok-service"
        />
        <label style={styles.label}>Username</label>
        <input
          style={styles.input}
          name="username"
          value={username}
          onChange={event => setUsername(event.target.value)}
          autoComplete="username"
        />
        <label style={styles.label}>Password</label>
        <div style={styles.passwordRow}>
          <input
            style={{ ...styles.input, flex: 1, margin: 0 }}
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={event => setPassword(event.target.value)}
            autoComplete={mode === 'bootstrap' ? 'new-password' : 'current-password'}
          />
          <button
            type="button"
            style={styles.passwordToggle}
            onClick={() => setShowPassword(prev => !prev)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {mode === 'bootstrap' && (
          <>
            <label style={styles.label}>Full name (optional)</label>
            <input
              style={styles.input}
              value={fullName}
              onChange={event => setFullName(event.target.value)}
              autoComplete="name"
            />
            <label style={styles.label}>Email (optional)</label>
            <input
              style={styles.input}
              value={email}
              onChange={event => setEmail(event.target.value)}
              autoComplete="email"
            />
            <label style={styles.label}>Licence activation key</label>
            <input
              style={styles.input}
              value={licenseActivationKey}
              onChange={event => setLicenseActivationKey(event.target.value)}
              autoComplete="off"
              required
            />
          </>
        )}
        {notice && <div style={styles.notice}>{notice}</div>}
        <button style={styles.button} type="submit" disabled={loading}>
          {loading ? (mode === 'bootstrap' ? 'Creating unit...' : 'Signing in...') : (mode === 'bootstrap' ? 'Create standalone unit' : 'Sign in')}
        </button>
      </form>
    </div>
  );
}

const RequireAuth: React.FC<{
  children: React.ReactElement;
  anyPermissions?: string[];
  allowRoles?: string[];
}> = ({ children, anyPermissions = [], allowRoles = [] }) => {
  const { isAuthenticated, hasAnyPermission, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to={LOGIN_ROUTE} replace />;
  }
  if (allowRoles.length > 0) {
    const userRole = String(user?.role || '').toUpperCase();
    if (!allowRoles.includes(userRole)) {
      return <Navigate to={defaultRouteForRole(user?.role)} replace />;
    }
  }
  if (anyPermissions.length > 0 && !hasAnyPermission(anyPermissions)) {
    return <Navigate to={defaultRouteForRole(user?.role)} replace />;
  }
  return children;
};

const PortalEntry: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  return <Navigate to={isAuthenticated ? defaultRouteForRole(user?.role) : LOGIN_ROUTE} replace />;
};

const SessionHeader: React.FC = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const location = useLocation();
  if (location.pathname === '/' || location.pathname === PLATFORM_SUPPORT_ROUTE) return null;
  if (!isAuthenticated || !user) return null;
  return (
    <div style={styles.sessionHeader}>
      <div style={styles.sessionText}>
        {user.fullName || user.username} ({user.roleLabel || user.role || 'User'}) - {user.organizationName || user.organizationSlug}
      </div>
      <button style={styles.sessionButton} onClick={() => void logout()}>
        Sign out
      </button>
    </div>
  );
};

const App: React.FC = () => {
  const { isLoading, user } = useAuth();
  const techPerms = useMemo(
    () => [
      'settings.read.own',
      'case.read.assigned',
      'case.assign',
      'document.upload',
      'library.maintain',
      'knowledge.query.unit',
      'metrics.read.group'
    ],
    []
  );
  const taskPerms = useMemo(() => ['task.run.assigned', 'case.assign'], []);

  useEffect(() => {
    document.title = 'bonzaiQ';
  }, []);

  if (isLoading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  return (
    <HashRouter>
      <div className="App" style={styles.appShell}>
        <UserActivityTracker />
        <SessionHeader />
        <div style={styles.routesShell}>
          <Routes>
            <Route path="/" element={<PublicLandingPage />} />
            <Route path={PORTAL_ROUTE} element={<PortalEntry />} />
            <Route path={LOGIN_ROUTE} element={<LoginPage />} />
            <Route path={PLATFORM_SUPPORT_ROUTE} element={<PlatformSupportConsole />} />
            <Route
              path={CUSTOMER_PORTAL_ROUTE}
              element={
                <RequireAuth allowRoles={['CUSTOMER']}>
                  <CustomerPortal />
                </RequireAuth>
              }
            />
            <Route
              path="/tech"
              element={
                <RequireAuth anyPermissions={techPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN', 'GLOBAL_ANALYST', 'WAREHOUSE_USER', 'DEVICE_EXPERT_USER']}>
                  <TechDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/tsfad/:caseId/remote"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <TSFADRemoteFlow />
                </RequireAuth>
              }
            />
            <Route
              path="/tsfad/:caseId"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <TSFADAnalysis />
                </RequireAuth>
              }
            />
            <Route
              path="/repair/:caseId"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <RepairTask />
                </RequireAuth>
              }
            />
            <Route
              path="/install/:caseId/pre"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <InstallPrepTask />
                </RequireAuth>
              }
            />
            <Route
              path="/install/:caseId/exec"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <InstallExecutionTask />
                </RequireAuth>
              }
            />
            <Route
              path="/install/:caseId/verify"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <InstallVerificationTask />
                </RequireAuth>
              }
            />
            <Route
              path="/install/:caseId/decom"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <InstallDecommissionTask />
                </RequireAuth>
              }
            />
            <Route
              path="/maintenance/:caseId/checkup"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <CheckUpTask />
                </RequireAuth>
              }
            />
            <Route
              path="/maintenance/:caseId/preventative"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <PreventativeMaintenanceTask />
                </RequireAuth>
              }
            />
            <Route
              path="/safety-upgrade/:caseId"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <SafetyUpgradeTask />
                </RequireAuth>
              }
            />
            <Route
              path="/safety-upgrade/:caseId/implementation"
              element={
                <RequireAuth anyPermissions={taskPerms} allowRoles={['TECH', 'SUPERUSER', 'ADMIN']}>
                  <SafetyUpgradeImplementationTask />
                </RequireAuth>
              }
            />
            <Route path="*" element={<Navigate to={defaultRouteForRole(user?.role)} replace />} />
          </Routes>
        </div>
        <InAppDialogHost />
      </div>
    </HashRouter>
  );
};

const styles: Record<string, React.CSSProperties> = {
  loading: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    fontFamily: 'system-ui, sans-serif'
  },
  loginWrap: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    background: '#f3f1ec',
    padding: '1.25rem'
  },
  loginCard: {
    width: '100%',
    maxWidth: 440,
    background: '#fffdf9',
    border: '1px solid #ded7ca',
    borderRadius: 16,
    padding: '1.5rem',
    display: 'grid',
    gap: '0.6rem',
    boxShadow: '0 18px 48px rgba(18, 48, 51, 0.12)'
  },
  loginErrorOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1500,
    display: 'grid',
    placeItems: 'center',
    padding: '1rem',
    background: 'rgba(10, 29, 31, 0.38)'
  },
  loginErrorCard: {
    width: 'min(420px, 96vw)',
    display: 'grid',
    gap: '0.85rem',
    padding: '1.15rem',
    background: '#fffdf9',
    border: '1px solid #ded7ca',
    borderRadius: 14,
    boxShadow: '0 20px 52px rgba(18, 48, 51, 0.22)'
  },
  loginErrorTitle: {
    margin: 0,
    color: '#123f42',
    fontSize: '1.15rem'
  },
  fieldRow: {
    display: 'flex',
    gap: '0.5rem'
  },
  toggleButton: {
    flex: 1,
    border: '1px solid #d6cec0',
    borderRadius: 8,
    padding: '0.45rem 0.6rem',
    background: '#fffdf9',
    color: '#173f42',
    cursor: 'pointer',
    fontWeight: 600
  },
  toggleButtonActive: {
    border: '1px solid #174f52',
    background: '#e4efec',
    color: '#123f42'
  },
  loginEyebrow: {
    color: '#9a6c2f',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.1em'
  },
  loginTitle: {
    margin: 0,
    color: '#123f42',
    fontSize: '1.55rem',
    lineHeight: 1.2
  },
  loginSubtitle: {
    marginTop: 0,
    marginBottom: '0.3rem',
    color: '#657476',
    fontSize: '0.92rem'
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#203f42'
  },
  input: {
    border: '1px solid #cec6b8',
    borderRadius: 8,
    padding: '0.62rem 0.7rem',
    background: '#ffffff',
    color: '#172f31'
  },
  passwordRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center'
  },
  passwordToggle: {
    border: '1px solid #cec6b8',
    borderRadius: 8,
    padding: '0.55rem 0.65rem',
    background: '#fffdf9',
    color: '#173f42',
    fontWeight: 600,
    cursor: 'pointer'
  },
  button: {
    marginTop: '0.5rem',
    border: 'none',
    borderRadius: 8,
    padding: '0.65rem 0.9rem',
    background: '#174f52',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600
  },
  error: {
    color: '#b42222',
    fontSize: '0.9rem'
  },
  notice: {
    color: '#7c4a03',
    background: '#fff4db',
    border: '1px solid #f2d19a',
    borderRadius: 8,
    padding: '0.55rem 0.65rem',
    fontSize: '0.9rem'
  },
  appShell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh'
  },
  routesShell: {
    flex: 1,
    minHeight: 0
  },
  sessionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 0.75rem',
    background: '#102a43',
    color: '#e7eff7'
  },
  sessionText: {
    fontSize: '0.84rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  sessionButton: {
    border: '1px solid #486581',
    background: '#243b53',
    color: '#fff',
    borderRadius: 6,
    padding: '0.32rem 0.6rem',
    cursor: 'pointer'
  }
};

export default App;
